import { TriangleAlertIcon } from "lucide-react";

import { Row } from "@/components/Row";
import { Text } from "@/components/Text";

import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { LastPing } from "@/types";

const DAYS_PER_MONTH = 30;
const DAYS_PER_YEAR = 365;

// Silence buckets, longest first. Below the shortest, no chip — they're fine.
const SILENCE_BUCKETS: { label: string; minDays: number; tone: Tone }[] = [
  { label: "No reply 1yr+", minDays: DAYS_PER_YEAR, tone: "danger" },
  { label: "No reply 6mo+", minDays: DAYS_PER_MONTH * 6, tone: "danger" },
  { label: "No reply 3mo+", minDays: DAYS_PER_MONTH * 3, tone: "warning" },
];

type Tone = "danger" | "warning";

const toneClassNames: Record<
  Tone,
  { chip: string; icon: string; text: string }
> = {
  danger: {
    chip: "border-red-200 bg-red-50",
    icon: "text-red-600",
    text: "text-red-700",
  },
  warning: {
    chip: "border-amber-200 bg-amber-50",
    icon: "text-amber-600",
    text: "text-amber-700",
  },
};

// "Never replied" needs this much outreach silence before it shows — a fresh
// first message with no reply yet isn't unresponsiveness.
const NEVER_REPLIED_MIN_DAYS = DAYS_PER_MONTH * 6;

// Fixed set of silence states from how long since the candidate last reached
// us. Never replied is its own state (gated on 6mo+ since our last message);
// under 3 months renders nothing.
export const SearchV2LastReplyChip = ({
  lastCommunication,
  lastInboundCommunication,
}: {
  // Most recent message either direction — with no inbound it's our outreach,
  // which is what "never replied" ages against.
  lastCommunication: LastPing | null;
  lastInboundCommunication: LastPing | null;
}) => {
  /* Helpers */
  const getState = (): { label: string; tone: Tone } | null => {
    if (!lastInboundCommunication) {
      if (!lastCommunication) return null;
      const outboundSilentDays = dates.calculateDaysAgo(lastCommunication.at);
      if (outboundSilentDays < NEVER_REPLIED_MIN_DAYS) return null;
      return { label: "Never replied", tone: "danger" };
    }
    const silentDays = dates.calculateDaysAgo(lastInboundCommunication.at);
    const bucket = SILENCE_BUCKETS.find((b) => silentDays >= b.minDays);
    return bucket ? { label: bucket.label, tone: bucket.tone } : null;
  };

  /* Render */
  const state = getState();
  if (!state) return null;
  const classNames = toneClassNames[state.tone];
  return (
    <Row
      className={cn(
        "h-5 shrink-0 items-center gap-1 rounded-full border px-2",
        classNames.chip,
      )}
    >
      <TriangleAlertIcon className={cn("h-3 w-3", classNames.icon)} />
      <Text className={cn("text-xs font-medium leading-none", classNames.text)}>
        {state.label}
      </Text>
    </Row>
  );
};
